import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import { getUserShortName } from 'helper';
import useError from 'components/ErrorProvider/useError';
import PersonalAccountChip from 'pages/PageRealEstatePersonal/components/PersonalAccountChip';
import { getPersonalList } from './api';
import { IRealEstatePersonalMember } from './interfaces';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    maxWidth: 1366,
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    zIndex: 1000,
  },
  table: {
    '& thead': {
      '& th': {
        fontWeight: 600,
        backgroundColor: '#FDD7E7',
      },
    },

    '& tbody': {
      '& tr': {
        '& td': {
          minWidth: 250,
          backgroundColor: '#FAFAFA',
          transition: 'background-color 200ms',
        },
        '&:hover': {
          '& td': {
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
  },
});

interface IProps {
  realEstateId: string;
}

const View: React.FC<IProps> = ({ realEstateId }) => {
  // Local state
  const [loading, setLoading] = useState(true);
  const [personalList, setPersonalList] = useState<IRealEstatePersonalMember[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Styles
  const classes = useStyles();

  // Global notification
  const { addError } = useError();

  useEffect(() => {
    if (!realEstateId) return;

    let active = true;
    setLoading(true);
    getPersonalList(realEstateId, page + 1, pageSize)
      .then(({ list, totalCount }) => {
        if (!active) return;
        setPersonalList(list);
        setTotalCount(totalCount);
      })
      .catch((error) => {
        if (!active) return;
        addError(error.response?.data?.message || 'Не удалось загрузить список персонала');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [realEstateId, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(0);
  }, [realEstateId]);

  return (
    <Box className={classes.root}>
      {loading ? (
        <Paper variant="outlined">
          <Box sx={{ position: 'relative', p: 5 }}>
            <Box className={classes.loader}>
              <CircularProgress />
            </Box>
          </Box>
        </Paper>
      ) : personalList.length ? (
        <TableContainer component={Paper} variant="outlined">
          <Table stickyHeader size="small" className={classes.table}>
            <TableBody>
              {personalList.map((personalMember) => {
                const { positionName, photoUrl, lastName, firstName, phone, partnerName } = personalMember;

                return (
                  <TableRow
                    key={personalMember.realEstatePersonalId || personalMember.personalId || personalMember.phone}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar src={photoUrl}>{getUserShortName(lastName, firstName)}</Avatar>

                        <Box sx={{ ml: 1 }}>
                          <Typography>
                            {lastName} {firstName}
                          </Typography>

                          <Typography variant="body2" color="textSecondary">
                            {positionName}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>{phone}</TableCell>
                    <TableCell>{partnerName}</TableCell>
                    {/* Без учётки человек в хрону не попадает: пусть это видно там же, где ведут персонал (SERVICE-5560). */}
                    <TableCell>
                      <PersonalAccountChip member={personalMember} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(Number.parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[20, 50, 100]}
            labelRowsPerPage="Сотрудников на странице"
          />
        </TableContainer>
      ) : (
        <Paper variant="outlined">
          <Box sx={{ p: 3 }}>
            <Typography align="center">Данные отсутствуют</Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default View;
